import React, { type FC, useCallback, useMemo } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import { useCoreChart } from "~/feature/chart/providers/CoreChartProvider";
import { ChartViewEnum } from "~/feature/chart/types";

import * as Styled from "./ChartViewMode.styles";
import { useChartViewModeOptions } from "./useChartViewModeOptions";

export const ChartViewMode: FC = () => {
  const { viewMode, setViewMode } = useCoreChart();
  const options = useChartViewModeOptions();

  const unavailableModes = useMemo(
    () =>
      new Set(
        options
          .filter(({ disabledTooltip }) => disabledTooltip)
          .map(({ value }) => value)
      ),
    [options]
  );

  const handleOnChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, viewMode: ChartViewEnum | null) => {
      if (viewMode && !unavailableModes.has(viewMode)) {
        setViewMode(viewMode);
      }
    },
    [setViewMode, unavailableModes]
  );

  return (
    <Styled.Group exclusive value={viewMode} onChange={handleOnChange}>
      {options.map(({ value, label, icon: Icon, disabledTooltip }) => {
        const button = (
          <Styled.Button
            key={value}
            value={value}
            data-view={value}
            $disabled={Boolean(disabledTooltip)}
          >
            {Icon && <Icon />}
            {label}
          </Styled.Button>
        );

        if (!disabledTooltip) {
          return button;
        }

        return (
          <RichTooltip key={value} title={disabledTooltip}>
            {button}
          </RichTooltip>
        );
      })}
    </Styled.Group>
  );
};
